<template>
  <app-layout>
    <template #header>
      <AppHead title="Product Settings" />
      <ShopNav 
        :categories="categories"
        categoryRoute="catalog.category"
        shopRoute="catalog"
        :shopButtons="false"
        :showDownloadProducts="true"
        :downloadProductsLoading="downloadProductsLoading"
        @download-products="downloadProducts"
      />
      <flash-messages />
    </template>

    <section id="productSettings" class="py-2">
      <DataTable :value="products" 
        responsiveLayout="stack" breakpoint="992px" :paginator="false"
                 v-model:filters="filters" filterDisplay="menu"
        class="p-datatable-sm" tableClass="border-left border-right border-bottom"
                 :globalFilterFields="['manufacturer']"
        v-model:expanded-rows="expandedBundles"
        @row-expand="onRowExpand" @row-collapse="onRowCollapse"
        :rowClass="() => { return 'table-row' }" stripedRows>
          <Column :expander="true" headerStyle="width: 3rem" bodyStyle="visibility: hidden;">
          </Column>
          <Column class="width-50 mb-3 mb-lg-0 d-flex d-lg-table-cell justify-content-start" field="id" header="Active">
            <template #body="slotProps">
              <input type="checkbox" :value="slotProps.data.id" :checked="slotProps.data.included" @change="toggleProduct($event, slotProps.data)" :key="slotProps.data.id" class="ml-3"/>
            </template>
          </Column>
          <Column class="width-50 mb-3 mb-lg-0" field="image_url" header="">
            <template #body="slotProps">
              <div class="flex-center">
                <img class="product-image" :src="slotProps.data.image_url" width="120"/>
              </div>
            </template>
          </Column>
          <Column class="name-col hide-title-mobile width-third" field="name" header="Name">
            <template #body="slotProps">
              <p class="mb-0"><span class="text-secondary">({{slotProps.data.sku}}) </span>{{slotProps.data.name}}</p>
              <div v-if="slotProps.data.is_bundle">
                <span class="badge badge-primary mx-1 is-bundle">
                  Bundle
                </span>
              </div>
            </template>
          </Column>
          <Column class="hide-title-mobile width-50" field="manufacturer" header="Manufacturer" filterMatchMode="contains">
            <template #body="slotProps">
              <span class="text-muted mb-2 mb-lg-0">{{slotProps.data.manufacturer}} </span>
            </template>
            <template #filter="{ filterModel }">
              <InputText v-model="filterModel.value" type="text" class="p-column-filter" placeholder="Search by manufacturer" />
            </template>
          </Column>
          <Column field="price_msrp" header="MSRP">
            <template #body="slotProps">
              {{ formatDollars(slotProps.data.price_msrp) }}
            </template>
          </Column>
          <Column field="wholesale_price" header="Wholesale">
            <template #body="slotProps">
              {{ formatDollars(slotProps.data.productPrice.price) }}
            </template>
          </Column>
          <Column field="user_price" header="Store" headerStyle="min-width: 120px">
            <template #body="slotProps">
              <div v-if="slotProps.data.is_bundle">
                <div class="d-flex align-items-center">
                  <input :disabled="true" type="text" class="form-control" size="3" v-model="slotProps.data.user_price"/>
                  <i class="mx-2 fa fa-check-circle text-success" v-if="slotProps.data.included_products.some(p => !p.updatingPriceLoading && p.updatingPriceSuccess)"></i>
                </div>
                <small v-if="bundleHasChangePrice(slotProps.data)" class="text-info text-small">
                  New: {{ formatDollars(slotProps.data.included_products.reduce((t, p) => t + p.qty * p.user_price, 0)) }}
                </small>
              </div>
              <div class="d-flex" v-else-if="slotProps.data.included">
                <div v-if="!slotProps.data.editing_price" @click="editPrice(slotProps.data)" class="text-primary cursor-pointer">
                  {{ formatDollars(slotProps.data.user_price) }}
                </div>
                <div v-else>
                  <input :disabled="slotProps.data.is_bundle" type="text" class="form-control" size="3" v-model="slotProps.data.user_price" @blur="updatePrice($event, products[slotProps.index])" @keyup.enter="slotProps.data.editing_price = false" v-focus/>
                </div>
                <span class="ml-2" v-if="slotProps.data.saving_price"><i class="fa fa-spin fa-spinner"></i></span>
                <span class="ml-2" v-if="slotProps.data.saved_price"><i class="fa fa-check-circle text-success"></i></span>
              </div>
            </template>
          </Column>
          <Column field="" header="On Truck #">
            <template #body="slotProps">
              <div class="d-flex" v-if="slotProps.data.included">
                <p v-if="slotProps.data.is_bundle" class="text-secondary mb-0"></p>
                <div v-else>
                  <div v-if="!slotProps.data.editing_inventory" @click="editInventory(products[slotProps.index])" class="text-primary cursor-pointer">
                    {{ slotProps.data.inventory_count }}
                  </div>
                  <div v-else>
                    <input  type="text" class="form-control" size="3" v-model="slotProps.data.inventory_count" @blur="updateInventory($event, products[slotProps.index])" @keyup.enter="slotProps.data.editing_inventory = false" v-focus/>
                  </div>
                  <span class="ml-2" v-if="slotProps.data.saving_inventory"><i class="fa fa-spin fa-spinner"></i></span>
                  <span class="ml-2" v-if="slotProps.data.saved_inventory"><i class="fa fa-check-circle text-success"></i></span>
                </div>
              </div>
            </template>
          </Column>
          <template #expansion="slotProps">
            <div class="mx-5 p-2 border">
              <h6>Bundle Items for {{slotProps.data.sku}}</h6>
              <DataTable :value="slotProps.data.included_products || []" responsiveLayout="scroll">
                <Column class="name-col hide-title-mobile width-third" field="name" header="Name">
                  <template #body="slotProps">
                    <p class="mb-0"><span class="text-secondary">({{slotProps.data.sku}}) </span>{{slotProps.data.name}}</p>
                  </template>
                </Column>
                <Column field="qty" header="Qty" :sortable="true"></Column>
                <Column field="price_msrp" header="Wholesale Price" :sortable="true">
                  <template #body="slotProps">
                    {{ formatDollars(slotProps.data.productPrice.price) }}
                  </template>
                </Column>
                <Column field="price_msrp" header="MSRP" :sortable="true">
                  <template #body="slotProps">
                    {{ formatDollars(slotProps.data.price_msrp) }}
                  </template>
                </Column>
                <Column field="user_price" header="Price" :sortable="true">
                  <template #body="slotProps">
                    <div class="d-flex align-items-center">
                      <input type="number" step=".01" class="form-control form-control-sm custom-form-inline" size="3" v-model="slotProps.data.user_price" @keydown="slotProps.data.updatingPrice=true;slotProps.data.updatingPriceSuccess=false" @change="updateUserProductBundle(slotProps.data)" />
                      <i class="mx-2 fa fa-spin fa-spinner" v-if="slotProps.data.updatingPriceLoading"/>
                      <i class="mx-2 fa fa-check-circle text-success" v-if="!slotProps.data.updatingPriceLoading && slotProps.data.updatingPriceSuccess"></i>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
        </template>
      </DataTable>
      <Paginator
        :first="(pagination.page-1)*pagination.per_page"
        :rows="pagination.per_page" 
        :totalRecords="pagination.total"
        :rowsPerPageOptions="[20,50,100]"
        @page="onPage($event)">
          <template #start="slotProps">
            <div class="font-weight-bold">Total: {{pagination.total}}</div>
          </template>
      </Paginator>
    </section>
  </app-layout>
</template>

<script>
  import AppLayout from '@/Layouts/AppLayout'
  import FlashMessages from '@/Shared/FlashMessages'
  import { FilterMatchMode, FilterOperator } from 'primevue/api';
  import InputText from 'primevue/inputtext';
  import Paginator from 'primevue/paginator'
  import JetInput from '@/Jetstream/Input'
  import JetCheckbox from '@/Jetstream/Checkbox'
  import JetInputError from '@/Jetstream/InputError'
  import JetLabel from '@/Jetstream/Label'
  import JetButton from '@/Jetstream/Button'
  import ShopNav from '@/Components/Nav/ShopNav'
  import CategoryBar from '@/Components/Nav/CategoryBar'
  import toastr from 'toastr'

  export default {
    components: {
      AppLayout,
      FlashMessages,
      JetInput,
      JetCheckbox,
      JetInputError,
      JetLabel,
      JetButton,
      Paginator,
      ShopNav,
      CategoryBar,
      InputText
    },
    props: {
      products: Array,
      pagination: Object,
      user: Object,
      categories: Array,
      category: Number
    },
    data() {
      return {
        priceForm: this.$inertia.form({
          _method: 'put',
          user_price: null,
          }),
        inventoryForm: this.$inertia.form({
          _method: 'put',
          inventory_count: null,
          }),
        productsForm: this.$inertia.form({
          _method: 'put',
          products: null,
          }),
        editPriceProduct: null,
        category_id: this.category,
        expandedRows: [],
        filters: {
          manufacturer: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
        expandedBundles: [],
        downloadProductsLoading: false,
      }
    },
    watch: {
      products() {
        this.expandedBundles = this.products.filter(p => p.is_bundle)
      },
    },
    mounted() {
      this.expandedBundles = this.products.filter(p => p.is_bundle)
    },
    methods: {
      onRowExpand(event) {
      },
      onRowCollapse(event) {
      },
      bundleHasChangePrice(product) {
        return product.included_products.length > 0 && 
          product.included_products.some(p => p.updatingPrice) &&
          Number(product.included_products.reduce((t, p) => t + p.qty * p.user_price, 0)).toFixed(2) != Number(product.user_price).toFixed(2)
      },
      onPage(e) {
        this.$inertia.reload({
          data: {page: e.page + 1, per_page: e.rows},
          only: ['products', 'pagination'],
        })
      },
      updateUserProductBundle(childProduct) {
        childProduct.updatingPriceLoading = true
        childProduct.updatingPriceSuccess = false
        axios.post(this.route('products.setUserPrice', childProduct.parent_product_id), {
            user_price: childProduct.user_price,
            included_product_id: childProduct.id,
          }).then((response) => {
            if (response.data.success) {
              childProduct.updatingPriceSuccess = true
              if (response.data.product) {
                const parent = this.products.find(p => p.id == childProduct.parent_product_id)
                if (parent) {
                  parent.user_price = response.data.product.user_price
                }
              }
            } else {
              toastr.error(response.data.message);
            }
          }).finally(() => {
            childProduct.updatingPriceLoading = false
            childProduct.updatingPrice = false
          });
      },
      toggleProduct(e, product) {
        if (e.target.checked) {
          axios.post(this.route('products.mtdInclude', product.id), {user_price: product.user_price}).then((response) => {
            if (response.data.success) {
              product.included = true;
              if (!product.inventory_count) product.inventory_count = 0;
              if (!product.user_price) product.user_price = product.price_msrp;
              this.editPrice(product);
              toastr.success("Product added to customer shop, you may adjust customer price.");
            } else {
              toastr.error(response.data.message);
              e.target.checked = false;
            }

          });
        } else {
          axios.post(this.route('products.mtdExclude', product.id)).then((response) => {
            if (response.data.success) {
              toastr.success("Product removed.");
              product.included = false;
            } else {
              toastr.error(response.data.message);
              e.target.checked = true;
            }
          });
        }
      },
      expandAll(product) {
        this.expandedRows =  {
          active: 1,
          backorder_enabled_mtd: 1,
          backorder_enabled_tu: 1,
          backorder_eta: 14,
          created_at: "2021-06-23T16:01:11.000000Z",
          current_inventory: 0,
          deleted_at: null
        }
      },
      editPrice(product) {
        if (this.editPriceProduct) {
          this.editPriceProduct.editing_price = false;
        }
        this.editPriceProduct = product;
        this.editPriceProduct.editing_price = true;
        this.editPriceProduct.saved_price = false;
      },
      updatePrice(e, product) {
        product.saved_price = false;
        product.editing_price = false;
        product.saving_price = true;

          axios.post(this.route('products.setUserPrice', product), {
                user_price: e.target.value
          }).then((response) => {
            if (response.data.success) {
              product.saving_price = false;
              product.saved_price = true;
            } else {
              toastr.error(response.data.message);
              product.user_price = 0;
              product.saving_price = false;
            }
          });
      },
      editInventory(product) {
        if (this.editInventoryProduct) {
          this.editInventoryProduct.editing_inventory = false;
        }
        this.editInventoryProduct = product;
        this.editInventoryProduct.saved_inventory = false;
        this.editInventoryProduct.editing_inventory = true;
      },
      updateInventory(e, product) {
        product.saved_inventory = false;
        product.editing_inventory = false;
        product.saving_inventory = true;

        axios.post(this.route('products.setInventory', product), {
          inventory_count: e.target.value
        }).then((response) => {
          product.saving_inventory = false;
          product.saved_inventory = true;
        })
      },
      save() {
        this.productsForm.products = JSON.stringify(this.products);
        this.productsForm.post(this.route('products.mtdupdate'))
      },
      downloadProducts() {
        this.downloadProductsLoading = true
        axios
          .post(
            this.route('mtd.products.download'),
            {},
            {
              responseType: 'blob'
            }
          )
          .then(response => {
            this.downloadResponse(response, 'products.xlsx')
          })
          .finally(() => {
            this.downloadProductsLoading = false
          })
      }
    },
    directives: {
      focus: {
        // directive definition
        mounted(el) {
          el.focus();
          el.select();
        }
      }
    }
  }
</script>

<style scoped>
  .product-image {
    height: 65px;
    max-width: 100%;
    object-fit: scale-down;
  }
  .pi-filter-icon {
    color: #fff;
  }
  
</style>
<style>
  tr:has(span.is-bundle) td:first-child {
    visibility: visible !important;
  }
  .p-datatable-row-expansion {
    border-bottom: 1px solid #dee2e6 !important;
  }
</style>

